import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Stack,
  Text,
  Flex,
} from "@chakra-ui/react";
import { Cloud, AlertCircle } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { CreateAlertFormValues } from "../types";
import { useStores } from "../../../stores/setup/use-store";
import { StoreState } from "../../../enum/StoreState";
import { InsightSelector } from "./InsightSelector";
import { MonitoredLocationPreview } from "./MonitoredLocationPreview";
import {
  ILocationMenuInsight,
  ILocationMenuInsightCondition,
} from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import {
  ReadOnlyConditionBuilder,
  EnvironmentalVariablesLoadingSkeleton,
  EnvironmentalVariablesErrorState,
} from "../../../components/ConditionBuilder/components";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { RestrictionTypeDisplay } from "./RestrictionTypeDisplay";

interface InsightAlertContentProps {
  values: CreateAlertFormValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  t: (key: string) => string;
  errors?: any;
  touched?: any;
  environmentalVariables?: IEnvironmentalVariable[];
}

// Empty state for when no insight is selected
const EmptyConditionsState: React.FC<{ t: (key: string) => string }> = ({
  t,
}) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    py={8}
    color="gray.500"
  >
    <AlertCircle size={32} color="#A0AEC0" />
    <Text fontSize="sm" mt={3} textAlign="center" fontStyle="italic">
      {t("selectInsightToSeeConditions")}
    </Text>
  </Flex>
);

export const InsightAlertContent: React.FC<InsightAlertContentProps> = observer(
  ({ values, setFieldValue, t, errors, touched, environmentalVariables }) => {
    const {
      dataStores: { operationRuleStore, insightAlertStore },
    } = useStores();

    const dashboards = operationRuleStore.operationRules;
    const isDashboardsLoading = operationRuleStore.state === StoreState.PEDDING;

    const locationMenuList = insightAlertStore.locationMenuList;
    const isInsightsLoading =
      insightAlertStore.locationMenuListState === StoreState.PEDDING;
    const selectedInsight = insightAlertStore.selectedInsight;

    // Track if we've already pre-selected the favorite dashboard
    const hasPreselectedFavoriteRef = useRef(false);

    // Pre-select favorite dashboard when dashboards are loaded
    useEffect(() => {
      // Only pre-select if:
      // 1. We haven't already pre-selected
      // 2. Dashboards are loaded
      // 3. No dashboard is currently selected
      if (
        !hasPreselectedFavoriteRef.current &&
        dashboards.length > 0 &&
        !values.dashboardId
      ) {
        const favoriteDashboard = dashboards.find((d) => d.isFavorite);
        if (favoriteDashboard) {
          hasPreselectedFavoriteRef.current = true;
          setFieldValue("dashboardId", favoriteDashboard.id);
          // Load insights for the favorite dashboard
          insightAlertStore.getLocationMenuListAPI(favoriteDashboard.id);
        }
      }
    }, [dashboards, values.dashboardId, setFieldValue, insightAlertStore]);

    // Convert conditions to format expected by ReadOnlyConditionBuilder
    const convertToReadOnlyFormat = useCallback(
      (rawConditions: ILocationMenuInsightCondition[]) => {
        if (!rawConditions || rawConditions.length === 0) return [];

        return rawConditions.map((group, index) => ({
          id: group.id || `group-${index}`,
          conditions: group.conditions.map((cond) => ({
            environmentalVariable: cond.environmentalVariable,
            environmentalVariableId: cond.environmentalVariableId,
            environmentalVariableName: cond.environmentalVariableName,
            comparisonOperator: cond.comparisonOperator,
            value: cond.value,
          })),
          groupOperator: (group.groupOperator || "and").toLowerCase(),
          conditionOperator: (group.conditionOperator || "and").toLowerCase(),
        }));
      },
      [],
    );

    // Get condition groups from selected insight and convert to read-only format
    const conditionGroups = useMemo(() => {
      if (!selectedInsight?.conditions) return [];
      return convertToReadOnlyFormat(selectedInsight.conditions);
    }, [selectedInsight, convertToReadOnlyFormat]);

    const hasConditions = conditionGroups.length > 0;

    // Find the selected location data (station or geofence)
    const selectedLocationData = useMemo(() => {
      if (!values.locationId || locationMenuList.length === 0) return null;

      const location = locationMenuList.find(
        (loc) => loc.id === values.locationId,
      );
      if (!location) return null;

      return {
        name: location.name,
        station: location.station,
        geofence: location.geofence,
      };
    }, [values.locationId, locationMenuList]);

    // Handle dashboard change
    const handleDashboardChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const dashboardId = e.target.value ? Number(e.target.value) : undefined;
        setFieldValue("dashboardId", dashboardId);
        // Clear insight selection when dashboard changes
        setFieldValue("insightId", undefined);
        setFieldValue("locationId", undefined);
        insightAlertStore.setSelectedInsight(null);

        // Fetch insights for selected dashboard
        if (dashboardId) {
          insightAlertStore.getLocationMenuListAPI(dashboardId);
        } else {
          insightAlertStore.setLocationMenuList([]);
        }
      },
      [setFieldValue, insightAlertStore],
    );

    // Handle insight selection
    const handleInsightSelect = useCallback(
      (insight: ILocationMenuInsight, locationId: number) => {
        setFieldValue("insightId", insight.id);
        setFieldValue("locationId", locationId);
        insightAlertStore.setSelectedInsight(insight);

        // Find the location name for the selected insight
        const location = locationMenuList.find((loc) => loc.id === locationId);
        if (location) {
          setFieldValue("area", location.name);
        }

        // Get template messages for title and description
        const TEMPLATE_MESSAGES = {
          extreme_wind: {
            title: t("extremeWindTitle"),
            description: t("possibilityOfImpacts"),
          },
          port_closure: {
            title: t("portClosureTitle"),
            description: t("potentialSuspension"),
          },
          extreme_rain: {
            title: t("extremeRainTitle"),
            description: t("possibilityOfFlooding"),
          },
        } as Record<string, { title: string; description: string }>;

        // Use template title and description based on alert type
        const alertType =
          values.templateType || values.alertType || "extreme_wind";
        const template =
          TEMPLATE_MESSAGES[alertType] || TEMPLATE_MESSAGES["extreme_wind"];
        setFieldValue("customTitle", template.title);

        // Always use translated template description
        setFieldValue("customDescription", template.description);
      },
      [
        setFieldValue,
        insightAlertStore,
        values.templateType,
        values.alertType,
        t,
        locationMenuList,
      ],
    );

    return (
      <Stack spacing={6}>
        {/* Select an Insight Section - Matching Figma Design */}
        <Box
          bg="white"
          border="1px solid"
          borderColor="#D4D4D8"
          borderRadius="8px"
          p={4}
        >
          {/* Section Header with cloud icon */}
          <Flex align="center" gap="16px" mb={4}>
            <Cloud size={20} color="#086AA9" />
            <Text
              fontSize="16px"
              lineHeight="24px"
              color="#086AA9"
              fontWeight="400"
            >
              {t("selectAnInsight")}
            </Text>
          </Flex>

          {/* Content container */}
          <Stack spacing={6}>
            {/* Selectors side by side + restriction type */}
            <Flex gap={4} align="start">
              {/* Dashboard Selector */}
              <Box flex="1">
                <FormControl>
                  <FormLabel color="#414651" fontWeight="500" fontSize="14px">
                    {t("dashboard")}
                  </FormLabel>
                  <Select
                    placeholder={t("selectDashboardPlaceholder")}
                    value={values.dashboardId || ""}
                    onChange={handleDashboardChange}
                    isDisabled={isDashboardsLoading}
                    borderColor="#D5D7DA"
                    _hover={{ borderColor: "#A1A1AA" }}
                    _focus={{
                      borderColor: "#0884CC",
                      boxShadow: "0 0 0 1px #0884CC",
                    }}
                    h="44px"
                    fontSize="16px"
                    color={values.dashboardId ? "#414651" : "#717680"}
                  >
                    {dashboards.map((dashboard) => (
                      <option key={dashboard.id} value={dashboard.id}>
                        {dashboard.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Insight Selector */}
              <Box flex="1">
                <FormControl>
                  <FormLabel color="#414651" fontWeight="500" fontSize="14px">
                    {t("insight")}
                  </FormLabel>
                  <InsightSelector
                    locations={locationMenuList}
                    selectedInsightId={values.insightId}
                    onSelect={handleInsightSelect}
                    placeholder={t("selectInsightPlaceholder")}
                    searchPlaceholder={t("searchInsightPlaceholder")}
                    isLoading={isInsightsLoading}
                    isDisabled={!values.dashboardId}
                    noResultsText={t("noInsightsFound")}
                    noInsightsInLocationText={t("noInsightsInLocation")}
                  />
                </FormControl>
              </Box>

              {/* Restriction Type Display - inline with selectors */}
              <Box flexShrink={0}>
                <RestrictionTypeDisplay
                  restrictionType={selectedInsight?.restrictionType}
                  t={t}
                />
              </Box>
            </Flex>

            {/* Conditions Section */}
            <Box>
              <Text
                color="#414651"
                fontWeight="500"
                fontSize="14px"
                lineHeight="20px"
                mb={2}
              >
                {t("selectedInsightConditions")}
              </Text>

              {/* Conditions content */}
              {insightAlertStore.isLoadingEnvironmentalVariables ? (
                <EnvironmentalVariablesLoadingSkeleton />
              ) : insightAlertStore.hasErrorLoadingEnvironmentalVariables ? (
                <EnvironmentalVariablesErrorState
                  errorMessage={t("errorLoadingEnvironmentalVariables")}
                  retryLabel={t("retryLoadEnvVariables")}
                  onRetry={() =>
                    insightAlertStore.retryLoadEnvironmentalVariables()
                  }
                />
              ) : hasConditions ? (
                <Box bg="#f4f4f5" borderRadius={8} p={6}>
                  <ReadOnlyConditionBuilder
                    conditionGroups={conditionGroups}
                    environmentalVariables={environmentalVariables}
                    t={t}
                  />
                </Box>
              ) : (
                <EmptyConditionsState t={t} />
              )}
            </Box>
          </Stack>
        </Box>

        {/* Monitored Location Preview */}
        {selectedLocationData && (
          <MonitoredLocationPreview
            locationName={selectedLocationData.name}
            station={selectedLocationData.station}
            geofence={selectedLocationData.geofence}
            t={t}
          />
        )}
      </Stack>
    );
  },
);
